import React, { createContext, useContext, useState, useEffect, useCallback } from 'react'
import { useAuth } from './AuthContext'
import { sendRequest } from '../../core/api/send.js'

const ThemeContext = createContext(null)

const DARK_THEME_COLOR = '#0a0f1c'
const LIGHT_THEME_COLOR = '#e4e7ee'

export function ThemeProvider({ children }) {
  // Initialize from localStorage (matches FOUC-prevention script in index.html)
  const [theme, setTheme] = useState(() => localStorage.getItem('theme') || 'dark')
  const { getAccessToken, isAuthenticated } = useAuth()

  // Apply/remove html.light class and update meta theme-color
  useEffect(() => {
    const root = document.documentElement
    const meta = document.querySelector('meta[name="theme-color"]')

    if (theme === 'light') {
      root.classList.add('light')
      if (meta) meta.setAttribute('content', LIGHT_THEME_COLOR)
    } else {
      root.classList.remove('light')
      if (meta) meta.setAttribute('content', DARK_THEME_COLOR)
    }
  }, [theme])

  // Sync from server on auth — may override localStorage if user changed theme on another device
  useEffect(() => {
    if (!isAuthenticated) return

    const syncFromServer = async () => {
      try {
        const token = getAccessToken()
        if (!token) return
        const res = await fetch('/api/user-preferences', {
          headers: { Authorization: `Bearer ${token}` }
        })
        const data = await res.json()
        if (data.success && data.preferences?.theme) {
          const serverTheme = data.preferences.theme
          if (serverTheme !== theme) {
            setTheme(serverTheme)
            localStorage.setItem('theme', serverTheme)
          }
        }
      } catch {
        // Best-effort — localStorage value is fine as fallback
      }
    }

    syncFromServer()
  }, [isAuthenticated]) // eslint-disable-line react-hooks/exhaustive-deps

  const toggleTheme = useCallback(() => {
    const next = theme === 'dark' ? 'light' : 'dark'
    setTheme(next)
    localStorage.setItem('theme', next)

    // Async server write (read-then-merge to avoid clobbering other prefs like moduleOrder)
    const token = getAccessToken()
    if (token) {
      fetch('/api/user-preferences', {
        headers: { Authorization: `Bearer ${token}` }
      })
        .then(res => res.json())
        .then(data => {
          const existing = (data.success && data.preferences) || {}
          return sendRequest('/api/user-preferences', {
            method: 'PUT',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${token}`
            },
            body: JSON.stringify({ preferences: { ...existing, theme: next } })
          })
        })
        .catch(() => {}) // Best-effort: a theme preference is not worth interrupting anyone for
    }
  }, [theme, getAccessToken])

  return (
    <ThemeContext.Provider value={{ theme, toggleTheme }}>
      {children}
    </ThemeContext.Provider>
  )
}

export function useTheme() {
  const context = useContext(ThemeContext)
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider')
  }
  return context
}

export default ThemeContext
